
import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Share2, Copy, Check } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { ShareLinkData } from "../../pt/utils/interfaces";

interface ShareDialogProps {
  clientID: string;
    clientName: string;
  shared?: ShareLinkData;
  
  onShare: (enabled:boolean) => Promise<ShareLinkData>;
}

export const ShareDialog = ({ clientID, clientName, shared, onShare }: ShareDialogProps) => {
  const isSharing = shared?.active || false;
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
 const code = shared?.code || "";
  // Generate a shareable link (in a real app, this would be generated by the backend)
  const shareLink = `${window.location.origin}/dashboard/${clientID}?code=${code}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Link copied",
        description: "Share link has been copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy link to clipboard",
        variant: "destructive",
      });
    }
  };

  const handleSharingToggle = async (enabled: boolean) => {
    const link = await onShare(enabled);
    console.log("Share link data:", link);
    if (enabled) {
      toast({
        title: "Sharing enabled",
        description: `Dashboard for ${clientName} is now shareable`,
      });
    } else {
      toast({
        title: "Sharing disabled",
        description: `Dashboard for ${clientName} is no longer shareable`,
      });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Share2 className="w-4 h-4 mr-2" />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Share Dashboard</DialogTitle>
          <DialogDescription>
            Share {clientName}'s measurement dashboard with others. When sharing is enabled, anyone with the link can view the measurements.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="sharing-toggle" className="text-base">
                Enable sharing
              </Label>
              <div className="text-sm text-gray-500">
                Allow others to view this dashboard
              </div>
            </div>
            <Switch
              id="sharing-toggle"
              checked={isSharing}
              onCheckedChange={handleSharingToggle}
            />
          </div>
          
          {isSharing && (
            <div className="space-y-2">
              <Label htmlFor="share-link">Share link</Label>
              <div className="flex gap-2">
                <Input
                  id="share-link"
                  value={shareLink}
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                Anyone with this link will be able to view the dashboard in read-only mode.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <DialogTrigger asChild>
            <Button variant="outline">Close</Button>
          </DialogTrigger>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
